import { useIsPersonUpdatesPage } from '@aibees/elephant/hooks/use-is-person-updates-page';
import { ROUTES } from '@aibees/elephant/router';
import {
  CopyToClipboard,
  Icon,
  Label,
  LabelColor,
  TextEllipsis,
} from '@aibees/shared/components';
import { useBoolean, useMatchRoute } from '@aibees/shared/hooks';
import { classnames } from '@aibees/shared/utils';
import { Box, Stack } from '@mui/material';
import { memo } from 'react';
import { getEmailType, howManyEmailsFound } from '../../utils';
import * as Styled from './styles';
import * as SX from './sx';
import type { EmailInfoProps as Props } from './types';
import {
  emailStatusFormatter,
  getDomainTypeChipColor,
  getStatusChipColor,
  humanReadableEmailType,
  type ChipColorParams,
} from './utils';

// TODO @maziar This should be refactored

export const EmailInfo = memo(
  ({
    className,
    data,
    kind,
    unlocked,
    reverify,
    hasUpdate,
    needsEnrichEmail,
  }: Props) => {
    const type = data && getEmailType(data);
    const notFound = !howManyEmailsFound(data ? [data] : [], {
      countInvalid: true,
    });
    const hover = useBoolean(false);
    const isUpdatePage = useIsPersonUpdatesPage();
    const isPersonPage = useMatchRoute(ROUTES.people);

    const chipColorParams: ChipColorParams = {
      status: data?.status,
      unlocked,
      needsEnrichEmail,
      hasUpdate,
      reverify,
    };
    const domainTypeChipColor = getDomainTypeChipColor(chipColorParams);
    const statusChipColor = getStatusChipColor(chipColorParams);

    const handleMouseLeave = () => {
      hover.onFalse();
    };

    const handleMouseEnter = () => {
      hover.onTrue();
    };

    return (
      <Styled.Wrapper
        className={classnames('EmailInfo', className)}
        sx={SX.root}
        onMouseLeave={handleMouseLeave}
        onMouseEnter={handleMouseEnter}
      >
        <Styled.Info sx={SX.info}>
          <Box
            display="flex"
            justifyItems="start"
            alignItems="center"
            gap={0.5}
            width="fill-available"
          >
            <Icon
              name={type === 'Work' ? 'email-outline' : 'person'}
              size={16}
              color="grey.500"
            />
            <TextEllipsis
              variant="body3"
              fontWeight={400}
              color="grey.700"
              maxWidth="150px"
            >
              {!unlocked || (hasUpdate && !needsEnrichEmail)
                ? '******@******'
                : notFound
                ? 'Email not found!'
                : data?.address}
            </TextEllipsis>
            <Box display="flex" alignItems="center" gap={0.5}>
              {!!data?.domainType && (
                <Label
                  variant="soft"
                  size="xsmall"
                  children={humanReadableEmailType(data?.domainType)}
                  color={domainTypeChipColor as LabelColor}
                />
              )}
            </Box>
          </Box>
        </Styled.Info>
        <Stack direction="row" gap={0.5}>
          {!notFound && unlocked && !(hasUpdate && !needsEnrichEmail) && (
            <CopyToClipboard
              value={data?.address ?? ''}
              sx={SX.copyIcon}
              icon={
                <Icon
                  name="copy"
                  size={16}
                  color={(p) => p.palette.grey[700]}
                />
              }
            />
          )}
          {!(unlocked && notFound) && (
            <Label
              variant="soft"
              size="xsmall"
              children={emailStatusFormatter({
                unlocked,
                needsEnrichEmail,
                hasUpdate,
                status: data?.status,
              })}
              color={statusChipColor as LabelColor}
            />
          )}
        </Stack>
      </Styled.Wrapper>
    );
  }
);

EmailInfo.displayName = 'EmailInfo';
